import React from "react";
import { Container, Grid2 } from "@mui/material";
import { motion } from "motion/react";
import "./forLawyers.css";
import grid1 from "./assets/grid1.png";
import grid2 from "./assets/grid2.png";
import grid3 from "./assets/grid3.png";
import grid4 from "./assets/grid4.png";
import layer1 from "./assets/layer1.png";
import layer2 from "./assets/layer2.png";
import layer3 from "./assets/layer3.png";
import lawyerHero from "./assets/lawyerHero.png";
import Footer from "../components/footer";
import TitleDiv from "../components/titlediv";
import PracticeSolutions from "./practiceSolutions";
import HomeHeader from "../home/new/components/homeHeader";
import { useNavigate } from "react-router-dom";

function ForLawyers() {
  const navigate = useNavigate();
  const cards = [
    {
      id: 1,
      title: "Attract More Clients",
      description:
        "Access a large pool of users actively seeking legal expertise. Quidpro.AI connects you with clients in need, helping expand your client base.",
      image: layer1,
    },
    {
      id: 2,
      title: "Engage With The Community",
      description:
        "Share your insights, articles, and legal tips to showcase your expertise and build trust with potential clients, establishing yourself as a reliable professional and attracting more business.",
      image: layer2,
    },
    {
      id: 3,
      title: "Gain Positive Reviews",
      description:
        "Collect reviews from your clients as you work with them, which will enhance your credibility and increase your visibility on the platform.",
      image: layer3,
    },
  ];

  return (
    <div className="for-lawyers">
      <HomeHeader
        isTitle={false}
        title={""}
        headerColor="header-white"
        navItems={true}
      />
      {/* Hero Section */}
      <motion.div
        className="lawyer-hero-section"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 1 }}
      >
        <div className="lawyer-hero-content">
          <motion.p
            className="lawyer-hero-heading"
            initial={{ y: -50 }}
            whileInView={{ y: 0 }}
            transition={{ type: "spring", stiffness: 100 }}
          >
            Unlock New Opportunities and Elevate Your Legal Practice
          </motion.p>

          <motion.p
            className="lawyer-hero-subtitle"
            initial={{ y: 50 }}
            whileInView={{ y: 0 }}
            transition={{ type: "spring", stiffness: 100 }}
          >
            Quidpro.ai connects lawyers with clients seeking legal help,
            offering tools to simplify scheduling, facilitate video
            consultations, and build your professional reputation. Join us to
            expand your practice and enhance your client service.
          </motion.p>

          {/* CTA Buttons Row */}

          <motion.p
            className="lawyer-hero-cta-button"
            initial={{ y: 50 }}
            whileInView={{ y: 0 }}
            transition={{ type: "spring", stiffness: 100 }}
            onClick={() => navigate("/grow/sign-up")}
          >
            List your practice on Quidpro
          </motion.p>
        </div>

        <motion.div
          className="lawyer-hero-image"
          style={{ zIndex: 1 }}
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ delay: 0.5, duration: 1 }}
        >
          <img src={lawyerHero} alt="Lawyer Illustration" />
        </motion.div>
      </motion.div>

      <div className="lawyer-video-section">
        <video
          src="/lawyer.mp4"
          autoPlay
          muted
          loop
          playsInline
          className="lawyer-hero-video"
        />
      </div>

      {/* Key Features Section */}
      <Container className="lawyer-key-features-section">
        <TitleDiv text="KEY FEATURES" />

        <motion.p
          className="section-title"
          initial={{ x: -100 }}
          whileInView={{ x: 0 }}
          transition={{ type: "spring", stiffness: 120 }}
        >
          Powerful Features For <br /> Effortless Legal Consultations
        </motion.p>
        <motion.p
          className="section-description"
          initial={{ x: 100 }}
          whileInView={{ x: 0 }}
          transition={{ type: "spring", stiffness: 120 }}
        >
          Our platform leverages AI-powered tools to make legal assistance
          faster and simpler, offering intuitive <br /> solutions to connect
          with experts and manage your consultations seamlessly.
        </motion.p>

        <Grid2
          container
          spacing={3}
          justifyContent="center"
          alignItems="flex-start"
          width="90vw"
        >
          {[
            {
              title: "Efficient Client Scheduling & Customizable Meetings",
              image: grid1,
              description:
                "Simplify client scheduling and customize consultations to fit your needs.",
            },
            {
              title: "Expand Your Reach & Engage with the Community",
              image: grid2,
              description:
                "Quidpro.ai connects you with clients seeking legal help.",
            },
            {
              title: "Secure & High-Quality Video Consultations",
              image: grid3,
              description:
                "Offer secure and professional communication through high-quality video calls.",
            },
            {
              title: "Efficient Document Management",
              image: grid4,
              description:
                "Easily manage and securely store legal documents, contracts, and case files.",
            },
          ].map((feature, index) => (
            <Grid2
              item
              xs={12}
              md={6}
              key={index}
              className="lawyer-key-feature-box"
            >
              <motion.img
                src={feature.image}
                alt={feature.title}
                className="lawyer-responsive-img"
                initial={{ y: 50 }}
                whileInView={{ y: 0 }}
                transition={{ type: "spring", stiffness: 120 }}
              />
              <motion.p
                className="lawyer-key-feature-title"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ delay: 0.2, duration: 1 }}
              >
                {feature.title}
              </motion.p>
              <motion.p
                className="lawyer-key-feature-description"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ delay: 0.3, duration: 1 }}
              >
                {feature.description}
              </motion.p>
            </Grid2>
          ))}
        </Grid2>
      </Container>

      {/* How It Works Section */}
      <Container className="lawyer-key-features-section">
        <TitleDiv text="HOW IT WORKS" />

        <motion.div
          className="lawyer-container"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 1 }}
        >
          {cards.map((card, index) => (
            <motion.div
              className={`card-for-lawyer ${
                index % 2 === 0 ? "card-layout-normal" : "card-layout-reverse"
              }`}
              key={card.id}
              initial={{ y: 50 }}
              whileInView={{ y: 0 }}
              transition={{ type: "spring", stiffness: 50 }}
            >
              <div className="card-content">
                <h2 className="card-title">{card.title}</h2>
                <p className="card-description">{card.description}</p>
              </div>
              <img
                src={card.image}
                alt={card.title}
                className="lawyer-card-image"
              />
            </motion.div>
          ))}
        </motion.div>
      </Container>

      {/* Additional Sections */}
      <PracticeSolutions />
      <Footer />
    </div>
  );
}

export default ForLawyers;
